import React from 'react';
import type { ReactNode } from 'react';
import { AlertSectionVariant } from '~/Blocks/AlertSectionBlock/AlertSectionVariant.csharp';
import { PageSectionHeading } from '~/Features/Layouts/Components/PageSectionHeading/PageSectionHeading';
import type { HtmlString } from '~/Features/Types/HtmlString.csharp';
import { AlertSection } from '~/Partials/AlertSection/AlertSection';
import { ButtonSize } from '~/Partials/Button/ButtonSize.csharp';
import { ButtonTier } from '~/Partials/Button/ButtonTier.csharp';
import { ButtonType } from '~/Partials/Button/ButtonType.csharp';
import { HtmlDiv } from '~/Partials/Controls/HtmlDiv';
import { asHtmlString } from '~/Partials/Controls/Utils/asHtmlString';
import { getGlobalHtmlStringFallback } from '~/Partials/Controls/Utils/globalHtmlStringFallback';
import { isHtmlString } from '~/Partials/Controls/Utils/isHtmlString';
import type { LinkProps } from '~/Partials/Link/Link';
import { Link } from '~/Partials/Link/Link';
import { LinkType } from '~/Partials/Link/LinkType.csharp';
import styles from './StudyModelSection.module.css';

export interface StudyModelSectionAccordionItemSettings {
    disabledMessageHeading: string;
    isDisabled?: boolean;
    disabledMessage?: string | HtmlString;
}

export interface StudyModelSectionProps {
    id: string;
    teaser: string;
    children: ReactNode;
    settings: StudyModelSectionAccordionItemSettings;
    heading?: HtmlString;
    studyProgramLink?: LinkProps & { label: string };
}

export const StudyModelSection: React.FC<React.PropsWithChildren<StudyModelSectionProps>> = props => {
    const { id, heading, teaser, studyProgramLink, settings, children } = props;

    const { isDisabled, disabledMessageHeading, disabledMessage } = { ...settings };

    const renderModelContent = () => {
        if (isDisabled) {
            const content = disabledMessage || getGlobalHtmlStringFallback('Study model is not available');
            const contentAsHtmlString = isHtmlString(content) ? content : asHtmlString(content);

            return (
                <AlertSection header={disabledMessageHeading} variant={AlertSectionVariant.Info}>
                    <HtmlDiv html={contentAsHtmlString} className="RichtextContent RichtextContent--dark" />
                </AlertSection>
            );
        }

        return children;
    };

    return (
        <section id={id} className={styles.StudyModelSection}>
            <div className={styles.HeaderWrapper}>
                <div className={styles.Header}>
                    {heading && <PageSectionHeading heading={heading} className={styles.Heading} />}
                    <div className={styles.TeaserWrapper}>
                        <span className={styles.Teaser}>{teaser}</span>
                    </div>
                </div>
            </div>
            <div className={styles.StudyModelContent}>{renderModelContent()}</div>
            {studyProgramLink && (
                <div className={styles.LinkWrapper}>
                    <Link
                        href={studyProgramLink.href}
                        className={styles.DescriptionLink}
                        type={LinkType.Button}
                        buttonType={ButtonType.Outline}
                        buttonTier={ButtonTier.Tertiary}
                        buttonSize={ButtonSize.Medium}
                    >
                        {studyProgramLink.label}
                    </Link>
                </div>
            )}
        </section>
    );
};
